import calculatePricesForContract, { ProductPricingInfo } from 'app/scripts/contract/calculatePricesForContract';
import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import { PageStructure } from 'assets/components/page/Page';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales, Language } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { date, defaultServerDateFormat, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { scrollToElement } from 'assets/utils/dom/Scroll';
import {
  getProductDocumentsFinalFields,
  getProductEnhancementsDocumentsFinalFields,
} from 'assets/utils/other/Utils.Other.GetDocumentFields';
import { isValidPhoneNumber } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import dayjs from 'dayjs';
import { compact, first, flatten, last, random, set, sumBy, uniqBy } from 'lodash';
import draftDeleteApi from 'models/core/draft/delete/Api.Draft.Delete';
import draftListApi from 'models/core/draft/list/Api.Draft.List';
import draftSaveApi from 'models/core/draft/save/Api.Draft.Save';
import mediaFileSave from 'models/core/mediaFile/save/Api.MediaFile.Save';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import ContractConsentRequestType from 'models/enums/ContractConsentRequestType';
import PurchaseState from 'models/enums/PurchaseState';
import PurchaseType from 'models/enums/PurchaseType';
import Product from 'models/productManagement/product/Model.Product';
import { productEnhancementListApi } from 'models/productManagement/productEnhancement/Model.ProductEnhancement';
import Vehicle from 'models/productManagement/vehicle/Model.Vehicle';
import VehicleModel from 'models/productManagement/vehicleModel/Model.VehicleModel';
import contractGenerateDocumentsApi from 'models/sales/contract/generateDocuments/Api.Contract.GenerateDocuments';
import revisionGenerateDocumentsApi from 'models/sales/contract/generateRevisionDocuments/Api.Contract.GenerateRevisionDocuments';
import Contract from 'models/sales/contract/Model.Contract';
import contractSaveApi from 'models/sales/contract/save/Api.Contract.Save';
import contractSendConsentApi from 'models/sales/contract/sendConsent/Api.Contract.SendConsent';
import contractSendDocumentForSigningApi from 'models/sales/contract/sendDocumentForSigning/Api.Contract.SendDocumentForSigning';
import contractProductDocumentDeleteApi from 'models/sales/contractProductDocuments/delete/Api.ContractProductDocument.Delete';
import contractProductDocumentSaveApi from 'models/sales/contractProductDocuments/save/Api.ContractProductDocument.Save';
import revisionProductDocumentSaveApi from 'models/sales/contractProductDocuments/saveRevision/Api.RevisionProductDocument.Save';
import distributorFindApi from 'models/sales/distributor/find/Api.Distributor.Find';
import Lender from 'models/sales/lender/Model.Lender';
import { useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { salesNewContractIndexRoute } from './Sales.NewContract.Index';

export type ContractExtraInfo = {
  createdAtLocal?: string;
  effectiveDate?: string;
  expiryDate?: string;
  termInMonths?: number;
  deductible?: number;
  maxVehiclePriceCAD?: number;
  minVehiclePriceCAD?: number;
  class?: string;
  warrantyOption?: string;
  postSale?: boolean;
  isPackage?: boolean;
  tier?: string;
  customInfo?: string;
  customInfoGroup?: string;
  pricePointId?: number;
  maxKm?: number;
  coverageKm?: number;
  maxCoverageKm?: number;
  salePriceCAD?: number;
  priceCAD?: number;
  minPriceCAD?: number;
  maxPriceCAD?: number;
  originalPriceCAD?: number;
  distributorPriceCAD?: number;
  surchargesPriceCAD?: number;
  enhancementsPriceCAD?: number;
  discountPriceCAD?: number;
  pricingDistributions?: Utils.PricingDistribution;
  distributorDistributions?: Utils.PricingDistribution;
  subfees?: Array<Utils.SurchargeInfo>;
  contractEnhancements?: Array<{
    priceCAD: number;
    originalPriceCAD: number;
    discountPriceCAD: number;
    distributorPriceCAD: number;
    pricingDistributions?: Utils.PricingDistribution;
    distributorDistributions?: Utils.PricingDistribution;
    productEnhancementId?: number;
    name?: string;
    maxPriceCAD?: number;
    minPriceCAD?: number;
  }>;
  totalPrice?: number;
  taxExempt?: boolean;
  additionalFields?: Array<{
    productDocumentId: number;
    key: string;
    value: any;
  }>;
  enhancementAdditionalFields?: Array<{
    productEnhancementDocumentId: number;
    key: string;
    value: any;
  }>;
  documentsFromTemplates?: Array<
    Partial<Model.IProductDocumentTemplate> & {
      notes?: string;
      formFile?: File;
      isDeleted?: boolean;
      uploadedId?: number; //Id of a previous IContractProductDocument
      mediaFileId?: number; //Id for IContractProductDocument.mediaFile from draft
    }
  >;

  dealerCost?: number;
  dealerCostTaxAmt?: number;
  dealerProfit?: number;
  dealerProfitTaxAmt?: number;
};
export type ContractProduct = Model.IProduct & ContractExtraInfo;
export type ProductForm = Utils.FormData<Model.IContract> & {
  documentLanguageIsoCode: Locale.Languages;
  products?: Array<ContractProduct>;
  distributor?: Model.IDistributor;
  draftId?: number;
};

export type SaveableContractData = Utils.FormData<Model.IContract> &
  ContractExtraInfo & {
    distributor?: Model.IDistributor;
    draftId?: number;
    product: Model.IProduct;
  };

export enum SalesNewContractAction {
  VEHICLE = 'vehicle',
  PRODUCTS = 'products',
  CLIENT = 'client',
  CONTRACT = 'contract',
  REVIEW = 'review',
  FINISH = 'finish',
}

export type ContractDraft = ProductForm & {
  dateUpdated: string;
};

const DRAFT_TYPE = 'Contract';
export enum ContractSaveStatus {
  waiting = 'contractStatusWaiting',
  saving = 'contractStatusSaving',
  generatingDocs = 'contractStatusGeneratingDocs',
  uploadingFiles = 'contractStatusUploadingFiles',
  done = 'contractStatusDone',
  sendingConsent = 'contractStatusSendingConsent',
  consentSent = 'contractStatusConsentSent',
  consentSentFailed = 'contractStatusConsentSentFailed',
  failed = 'contractStatusFailed',
}

export default function useSalesNewContract() {
  const { lang } = getLocales();
  const { permissions, userId } = useAuthStore();
  const pageRouter = usePageRouter<Module.Sales.NewContract.Params, Module.Sales.NewContract.Query>({
    route: salesNewContractIndexRoute,
  });
  const [contractStatuses, setContractStatuses] = useState<
    Array<{
      contract: SaveableContractData;
      status: ContractSaveStatus;
      documentCount: number;
      uploadedDocuments: number;
      areWeGeneratingDocuments: boolean;
      areDocumentsGenerated: boolean;
      areDocumentsAutomaticallySentForSigning: boolean;
      isPaymentRequired: boolean;
      isSignRequired: boolean;
      isCustomerConsentRequired: boolean;
      areWeSendingConsent: boolean;
      isConsentSent: boolean;
    }>
  >([]);
  const id = pageRouter.params.id && !isNaN(parseInt(pageRouter.params.id)) ? parseInt(pageRouter.params.id) : null;
  const contractFindApi = useApi({
    action: Contract.find,
    wait: !id,
    body: {
      id: id ?? 0,
    },
  });

  const isEffectiveDateRestricted = Boolean(permissions.RESTRICT_EDIT_EFFECTIVEDATE_CONTRACT);
  const isEffectiveDateValid = (effectiveDate: string | number | dayjs.Dayjs | Date) => {
    const today = dayjs().startOf('day');
    const effDate = dayjs(effectiveDate).startOf('day');

    return effDate.isBetween(today.subtract(5, 'days'), today.add(5, 'days'), null, '[]');
  };
  const isVehicleInServiceDateValid = (vehicle: Utils.FormData<Model.IVehicle>) => {
    const yearOffset = 1;
    if (!vehicle?.inServiceDate) return true;
    if (!vehicle?.year) return false;

    const inServiceYear = dayjs(vehicle?.inServiceDate).year();
    const year = vehicle?.year;

    const minYear = year - yearOffset;
    const maxYear = year + yearOffset;

    return inServiceYear >= minYear && inServiceYear <= maxYear;
  };

  //#region Data editing
  useEffect(() => {
    (async () => {
      if (!contractFindApi.payload) return;

      const vehicleModels = await VehicleModel.list({
        makeCode: contractFindApi.payload.vehicle.makeCode,
        modelCode: contractFindApi.payload.vehicle.model,
      });
      const vehicleModel = first(vehicleModels.payload?.data || [])?.toJSON();
      const dealerFind = await Dealer.find({ id: contractFindApi.payload.dealerId });
      const productFind = await Product.find({ id: contractFindApi.payload.productId });
      const lenderFind = await Lender.find({ id: contractFindApi.payload.vehicle?.lenderId });
      productChangeDependencies.current = null;
      form.update({
        ...contractFindApi.payload,
        dealer: dealerFind.payload,
        vehicle: {
          ...contractFindApi.payload?.vehicle,
          lender: lenderFind.payload,
          purchaseState:
            contractFindApi.payload?.vehicle.purchaseState === PurchaseState.USED
              ? PurchaseState.PRE_OWNED
              : contractFindApi.payload?.vehicle.purchaseState,
          vehicleModel,
        },
        product: productFind.payload,
        products: !productFind.payload
          ? []
          : [
              {
                ...productFind.payload,
                createdAtLocal: contractFindApi.payload.createdAtLocal,
                effectiveDate: contractFindApi.payload.effectiveDate,
                expiryDate: contractFindApi.payload.expiryDate,
                termInMonths: contractFindApi.payload.termInMonths,
                customInfo: contractFindApi.payload.customInfo,
                customInfoGroup: contractFindApi.payload.customInfoGroup,
                minVehiclePriceCAD: contractFindApi.payload.minVehiclePriceCAD,
                maxVehiclePriceCAD: contractFindApi.payload.maxVehiclePriceCAD,
                class: contractFindApi.payload.class,
                warrantyOption: contractFindApi.payload.warrantyOption,
                postSale: contractFindApi.payload.postSale,
                isPackage: contractFindApi.payload.isPackage,
                tier: contractFindApi.payload.tier,
                maxKm: contractFindApi.payload.maxKm,
                coverageKm: contractFindApi.payload.coverageKm,
                deductible: contractFindApi.payload.deductible,
                salePriceCAD: contractFindApi.payload.salePriceCAD,
                priceCAD: contractFindApi.payload.priceCAD,
                distributorPriceCAD: contractFindApi.payload.distributorPriceCAD,
                surchargesPriceCAD: contractFindApi.payload.surchargesPriceCAD,
                enhancementsPriceCAD: contractFindApi.payload.contractEnhancements?.reduce((p, c) => p + c.priceCAD, 0),
                discountPriceCAD: contractFindApi.payload.discountPriceCAD,
                originalPriceCAD: contractFindApi.payload.originalPriceCAD,
                pricingDistributions: contractFindApi.payload.pricingDistributions,
                distributorDistributions: contractFindApi.payload.distributorDistributions,
                subfees: contractFindApi.payload.subfees,
                contractEnhancements: contractFindApi.payload.contractEnhancements,
                totalPrice: contractFindApi.payload.salePriceWithTaxCAD,
                taxExempt: contractFindApi.payload.client.taxExempt,
                documentsFromTemplates: [
                  ...(productFind.payload.productDocumentTemplates?.map((it) => {
                    const alreadyUploaded = contractFindApi.payload.productDocuments?.find((d) => d.name === it.name);
                    return {
                      ...it,
                      uploadedId: alreadyUploaded?.id,
                      notes: alreadyUploaded?.notes,
                    };
                  }) ?? []),
                  ...(contractFindApi.payload.productDocuments
                    ?.filter((d) => !productFind.payload.productDocumentTemplates?.some((t) => t.name === d.name))
                    ?.map((d) => ({
                      name: d.name,
                      notes: d.notes,
                      isDeleted: false,
                      uploadedId: d.id,
                    })) ?? []),
                ],
              },
            ],
      });
    })();
  }, [contractFindApi.payload]);
  const form = useForm<ProductForm>({
    default: { products: [], draftId: random(false), isPackage: false, documentLanguageIsoCode: Language.english },
    onSubmit: async ({ products, ...contract }) => {
      const contracts =
        products?.map(
          ({
            termInMonths,
            maxKm,
            deductible,
            customInfo,
            customInfoGroup,
            minVehiclePriceCAD,
            maxVehiclePriceCAD,
            class: vehicleClass,
            tier,
            coverageKm,
            maxCoverageKm,
            warrantyOption,
            postSale,
            isPackage,
            isCustomerConsentRequired,
            priceCAD,
            distributorPriceCAD,
            surchargesPriceCAD,
            enhancementsPriceCAD,
            discountPriceCAD,
            originalPriceCAD,
            minPriceCAD,
            maxPriceCAD,
            salePriceCAD,
            pricingDistributions,
            distributorDistributions,
            expiryDate,
            effectiveDate,
            createdAtLocal,
            subfees,
            contractEnhancements,
            additionalFields,
            enhancementAdditionalFields,
            documentsFromTemplates,
            documentSigningType,
            ...product
          }) => ({
            ...contract,
            vehicle: {
              ...contract.vehicle,
              purchaseState:
                contract.vehicle?.purchaseState === PurchaseState.PRE_OWNED
                  ? PurchaseState.USED
                  : contract.vehicle?.purchaseState,
            },
            minVehiclePriceCAD,
            maxVehiclePriceCAD,
            class: vehicleClass,
            warrantyOption,
            tier,
            product,
            termInMonths,
            customInfo,
            customInfoGroup,
            maxKm,
            coverageKm,
            maxCoverageKm,
            postSale,
            isPackage,
            isCustomerConsentRequired,
            deductible,
            priceCAD,
            distributorPriceCAD,
            surchargesPriceCAD,
            enhancementsPriceCAD,
            discountPriceCAD,
            originalPriceCAD,
            minPriceCAD,
            maxPriceCAD,
            salePriceCAD,
            pricingDistributions,
            distributorDistributions,
            expiryDate,
            effectiveDate,
            createdAtLocal: !createdAtLocal ? dayjs().format(defaultServerDateTimeFormat) : createdAtLocal,
            subfees,
            contractEnhancements,
            additionalFields,
            enhancementAdditionalFields,
            documentsFromTemplates,
            documentSigningType,
          })
        ) ?? [];

      setContractStatuses(
        contracts.map((contract) => ({
          contract,
          status: ContractSaveStatus.waiting,
          documentCount: 0,
          areWeGeneratingDocuments: false,
          areDocumentsGenerated: false,
          areDocumentsSigned: false,
          areDocumentsAutomaticallySentForSigning: false,
          isPaymentRequired: false,
          isSignRequired: false,
          isCustomerConsentRequired: contract?.isCustomerConsentRequired,
          areWeSendingConsent: false,
          isConsentSent: false,
          uploadedDocuments: 0,
        }))
      );

      for (let index = 0; index < contracts.length; index++) {
        const currentContract = contracts[index];

        const { additionalFields, enhancementAdditionalFields, documentsFromTemplates, ...contract } = currentContract;
        const transferableDocs = documentsFromTemplates?.filter((it) => it.mediaFileId && !it.isDeleted);
        const uploadableDocs = documentsFromTemplates?.filter(
          (it) => !it.mediaFileId && (it.formFile || (it.isDeleted && it.uploadedId))
        );
        const productDocuments = contract.product?.productDocuments ?? [];
        const hasSignatureFields = !!productDocuments?.some((it) => !!it.signatureFields?.length);
        setContractStatuses((old) => {
          old[index].status = ContractSaveStatus.saving;
          old[index].documentCount = (uploadableDocs?.length ?? 0) + (transferableDocs?.length ?? 0);
          old[index].areWeGeneratingDocuments = !!productDocuments.length;
          old[index].isPaymentRequired =
            !contract.revisionNumber && !contract.isPayed ? contract.dealer?.isPaymentRequired : false;
          old[index].isSignRequired =
            !contract.revisionNumber &&
            !contract.isSignedWithDocuSing &&
            !contract.isSignedWithSignFlow &&
            !contract.isSentForSigning
              ? contract.product?.isSendDocumentToSigningEnabled && hasSignatureFields
              : false;
          return [...old];
        });
        contract.productDocuments = transferableDocs?.map((doc) => ({
          name: doc.name,
          notes: doc.notes,
          mediaFileId: doc.mediaFileId,
        }));
        const contractResponse = await contractSaveApi(contract);
        if (contractResponse.status !== HttpStatus.ok) {
          addLog({ error: contractResponse.message });
        }
        if (transferableDocs?.length) {
          setContractStatuses((old) => {
            old[index].uploadedDocuments = transferableDocs?.length ?? 0;
            return [...old];
          });
        }
        const contractId = contractResponse?.payload;
        if (contractId) {
          const refetchedContractResponse = await Contract.find({ id: contractId });
          const refetchedContract = refetchedContractResponse?.payload;
          if (refetchedContract?.product?.isCustomerConsentRequired) {
            setContractStatuses((old) => {
              old[index].contract = refetchedContract;
              old[index].status = ContractSaveStatus.done;
              old[index].isCustomerConsentRequired = true;
              return [...old];
            });
          } else {
            if (refetchedContract) {
              if (uploadableDocs?.length) {
                setContractStatuses((old) => {
                  old[index].status = ContractSaveStatus.uploadingFiles;
                  old[index].contract = refetchedContract;
                  return [...old];
                });
                for (const doc of uploadableDocs) {
                  let wasItSuccessfull = false;
                  if (doc.formFile) {
                    const docUploadResponse = refetchedContract.revisionNumber
                      ? await revisionProductDocumentSaveApi({
                          formFile: doc.formFile,
                          name: doc.name,
                          notes: doc.notes,
                          contractId: refetchedContract.id,
                        })
                      : await contractProductDocumentSaveApi({
                          formFile: doc.formFile,
                          name: doc.name,
                          notes: doc.notes,
                          contractId: refetchedContract.id,
                        });
                    wasItSuccessfull = docUploadResponse.status === HttpStatus.ok;
                  }
                  if (doc.isDeleted && doc.uploadedId) {
                    const docDeleteResponse = await contractProductDocumentDeleteApi({
                      id: refetchedContract.id,
                      documentId: doc.uploadedId,
                    });
                    wasItSuccessfull = docDeleteResponse.status === HttpStatus.ok;
                  }
                  if (wasItSuccessfull) {
                    setContractStatuses((old) => {
                      old[index].uploadedDocuments += 1;
                      return [...old];
                    });
                  }
                }
              }
              const refetchedLender = (await Lender.find({ id: refetchedContract.vehicle?.lenderId }))?.payload;
              const remmapedContract = {
                ...refetchedContract,
                vehicle: {
                  ...refetchedContract.vehicle,
                  lender: refetchedLender,
                },
              };
              const finalFields = getProductDocumentsFinalFields(remmapedContract, productDocuments, additionalFields);
              const finalEnhancementFields = getProductEnhancementsDocumentsFinalFields(
                remmapedContract,
                enhancementDocs,
                enhancementAdditionalFields
              );

              if (finalFields?.length || finalEnhancementFields?.length) {
                setContractStatuses((old) => {
                  old[index].status = ContractSaveStatus.generatingDocs;
                  return [...old];
                });
                const docGenerationResponse = refetchedContract.revisionNumber
                  ? await revisionGenerateDocumentsApi({
                      contractId: refetchedContract.id,
                      productPdfInputItems: finalFields,
                      productEnhancementPdfInputItems: finalEnhancementFields,
                    })
                  : await contractGenerateDocumentsApi({
                      contractId: refetchedContract.id,
                      productPdfInputItems: finalFields,
                      productEnhancementPdfInputItems: finalEnhancementFields,
                    });
                if (docGenerationResponse?.status === HttpStatus.ok) {
                  setContractStatuses((old) => {
                    old[index].areDocumentsGenerated = true;
                    return [...old];
                  });
                }
              }

              const { payload: finalContract } = await Contract.find({ id: contractId });
              const isPaymentRequired =
                !contract.revisionNumber && !contract.isPayed ? contract.dealer?.isPaymentRequired : false;
              if (
                !finalContract.isSignedWithDocuSing &&
                !finalContract.isSignedWithSignFlow &&
                !finalContract.isSentForSigning &&
                !isPaymentRequired &&
                hasSignatureFields &&
                contract.product?.isSendDocumentToSigningEnabled
              ) {
                const docSigningResponse = await contractSendDocumentForSigningApi({
                  contractId: finalContract.id,
                });

                if (docSigningResponse?.status === HttpStatus.ok) {
                  setContractStatuses((old) => {
                    old[index].areDocumentsAutomaticallySentForSigning = true;
                    return [...old];
                  });
                }
              }
              setContractStatuses((old) => {
                old[index].contract = finalContract;
                old[index].status = ContractSaveStatus.done;
                return [...old];
              });
            } else {
              setContractStatuses((old) => {
                old[index].status = ContractSaveStatus.failed;
                return [...old];
              });
            }
          }
        } else {
          setContractStatuses((old) => {
            old[index].status = ContractSaveStatus.failed;
            return [...old];
          });
        }
      }
      await deleteDraft();
    },
    validation: (data, errors) => {
      if (
        data.products?.some((p) => {
          const enhancementsMinPriceCAD = sumBy(p?.contractEnhancements, (it) => it.minPriceCAD ?? 0) || 0;
          const enhancementsMaxPriceCAD =
            sumBy(p?.contractEnhancements, (it) => {
              const enhPaymentPriceCAD = it.originalPriceCAD + it.distributorPriceCAD + it.discountPriceCAD;

              if (p.maximumMarkup && !p.maxPriceCAD) return enhPaymentPriceCAD;
              return it.maxPriceCAD || enhPaymentPriceCAD;
            }) || 0;

          const minSalePriceCAD = p?.minPriceCAD + enhancementsMinPriceCAD;
          const maxSalePriceCAD = p?.maxPriceCAD + enhancementsMaxPriceCAD + (p?.surchargesPriceCAD || 0);

          const productWithAdditionaPricesCAD =
            p?.originalPriceCAD +
            p?.discountPriceCAD +
            p?.distributorPriceCAD +
            (p?.surchargesPriceCAD || 0) +
            enhancementsMaxPriceCAD;

          const maxSalePriceForMarkupCAD =
            productWithAdditionaPricesCAD * p?.maximumMarkup + productWithAdditionaPricesCAD;

          return p.maxPriceCAD
            ? p.salePriceCAD < minSalePriceCAD
              ? true
              : p.salePriceCAD > maxSalePriceCAD && maxSalePriceCAD > minSalePriceCAD
                ? true
                : false
            : p?.maximumMarkup
              ? p.salePriceCAD > maxSalePriceForMarkupCAD
                ? true
                : false
              : false;
        })
      ) {
        errors.salePriceCAD = lang.invalidForm;
      }
      if (!data.client?.phone) set(errors, 'client.phone', lang.mustNotBeEmpty);
      if (data.client?.phone && !isValidPhoneNumber(data.client?.phone))
        set(errors, 'client.phone', lang.invalidFormatRequires.replace('{format}', '###-###-####'));
      if (!data.client?.firstName) set(errors, 'client.firstName', lang.mustNotBeEmpty);
      if (!data.client?.lastName) set(errors, 'client.lastName', lang.mustNotBeEmpty);
      if (!data.client?.email) set(errors, 'client.email', lang.mustNotBeEmpty);
      if (!data.client?.streetAddress) set(errors, 'client.streetAddress', lang.mustNotBeEmpty);
      if (!data.client?.city) set(errors, 'client.city', lang.mustNotBeEmpty);
      if (!data.client?.provinceCode) set(errors, 'client.provinceCode', lang.mustNotBeEmpty);
      if (!data.client?.postalCode) set(errors, 'client.postalCode', lang.mustNotBeEmpty);
      if (!data.client?.countryCode) set(errors, 'client.countryCode', lang.mustNotBeEmpty);
      if (data.client?.taxExempt && !data.client?.taxExemptionBand)
        set(errors, 'client.taxExemptionBand', lang.mustNotBeEmpty);

      if (!data.dealer) set(errors, 'dealer._objectError', lang.mustNotBeEmpty);

      if (!data.vehicle) set(errors, 'vehicle._objectError', lang.mustNotBeEmpty);
      if (!data.vehicle?.vin) set(errors, 'vehicle.vin', lang.mustNotBeEmpty);
      if (data.vehicle?.vin?.length < 8) set(errors, 'vehicle.vin', lang.vinMustBeAtLeast8CharsLong);
      if (!data.vehicle?.makeCode) set(errors, 'vehicle.makeCode', lang.mustNotBeEmpty);
      if (!data.vehicle?.model) set(errors, 'vehicle.model', lang.mustNotBeEmpty);
      if (!data.vehicle?.year) set(errors, 'vehicle.year', lang.mustNotBeEmpty);
      if (
        data.vehicle?.purchaseType &&
        !(
          data.vehicle?.purchaseType === PurchaseType.CASH || data.vehicle?.purchaseType === PurchaseType.LINE_OF_CREDIT
        ) &&
        !data.vehicle?.lenderId
      )
        set(errors, 'vehicle.lenderId', lang.mustNotBeEmpty);
      if (data.vehicle?.purchaseType === PurchaseType.FINANCE && !data.vehicle?.financedAmount)
        set(errors, 'vehicle.financedAmount', lang.mustNotBeEmpty);
      if (data.product?.isPurchaseDetailsVisible && !data.vehicle?.purchaseState)
        set(errors, 'vehicle.purchaseState', lang.mustNotBeEmpty);
      if (!data.vehicle?.inServiceDate && data.vehicle?.purchaseState === PurchaseState.NEW)
        set(errors, 'vehicle.inServiceDate', lang.mustNotBeEmpty);
      if (data.vehicle?.inServiceDate && !isVehicleInServiceDateValid(data.vehicle)) {
        set(errors, 'vehicle.inServiceDate', lang.vehicleInServiceDateValidationMsg);
      }
      if (
        !data.vehicle?.purchasePrice &&
        (data.product?.isPurchaseDetailsVisible || data.dealer?.products?.some((p) => p.isPurchaseDetailsVisible))
      )
        set(errors, 'vehicle.purchasePrice', lang.mustNotBeEmpty);
      if (!data.products?.some((p) => p.id)) set(errors, 'products._objectError', lang.mustNotBeEmpty);
      if (data.products?.some((p) => !p.expiryDate)) errors.expiryDate = lang.mustNotBeEmpty;
      if (!data.vehicle?.posOdometerReading) set(errors, 'vehicle.posOdometerReading', lang.mustNotBeEmpty);
      if (
        data.products?.some(
          (p) =>
            !!p.documentsFromTemplates?.length &&
            p.documentsFromTemplates?.some(
              (t) =>
                !!t.id &&
                (t.uploadedId || t.mediaFileId
                  ? t.isDeleted
                    ? !t.formFile && t.required
                    : false
                  : !t.formFile && t.required)
            )
        )
      )
        set(errors, 'products.documentsFromTemplates', lang.notAllRequiredDocumentInfoWasProvided);

      if (data.products?.some((p) => !p.effectiveDate)) errors.effectiveDate = lang.mustNotBeEmpty;

      const isNew = data.vehicle?.purchaseState?.toUpperCase()?.endsWith('NEW');
      if (
        (!data?.id || isEffectiveDateRestricted) &&
        data.products?.some((p) => p.effectiveDate && !isEffectiveDateValid(p.effectiveDate)) &&
        !isNew
      ) {
        set(
          errors,
          'effectiveDate',
          replaceStringTemplateWithValues(lang.dateMustBeInNDayRangeFromToday, { n: String(5) })
        );
      }
    },
  });

  const productEnhancementList = useApi({
    action: productEnhancementListApi,
  });
  const enhancements = productEnhancementList.payload?.data ?? [];
  const enhancementDocs = uniqBy(
    flatten(
      compact(flatten(form.data.products?.map((it) => it.contractEnhancements)))?.map(
        (ce) => enhancements?.find((e) => e?.id === ce?.productEnhancementId)?.productEnhancementDocuments
      )
    ),
    (it) => it?.id
  );

  const draftApi = useApi({ action: draftListApi, body: { entity: DRAFT_TYPE } });
  useEffect(() => {
    const draft = draftApi.payload?.data?.find((d) => d?.id === pageRouter.query?.draftId);
    if (draft) {
      productChangeDependencies.current = null;
      const draftForm = draft.data as ProductForm;
      setSearchedVin(draftForm?.vehicle?.vin);
      form.set(draftForm);
    }
  }, [draftApi.payload, pageRouter.query.draftId]);
  const productChangeDependencies = useRef({ vehicle: form.data.vehicle, dealerId: form.data.dealerId });
  useEffect(() => {
    const prevVehicle = productChangeDependencies.current;
    if (
      !contractFindApi.payload &&
      (prevVehicle?.vehicle?.posOdometerReading !== form.data.vehicle?.posOdometerReading ||
        prevVehicle?.vehicle?.vehicleModel?.id !== form.data.vehicle?.vehicleModel?.id ||
        prevVehicle?.vehicle?.purchaseState !== form.data.vehicle?.purchaseState ||
        prevVehicle?.vehicle?.purchasePrice !== form.data.vehicle?.purchasePrice ||
        prevVehicle?.dealerId !== form.data.dealerId)
    ) {
      if (prevVehicle) form.update({ products: [] });
      productChangeDependencies.current = { vehicle: form.data.vehicle, dealerId: form.data.dealerId };
    }
  }, [
    contractFindApi.payload,
    form.data.vehicle?.posOdometerReading,
    form.data.vehicle?.vehicleModel?.id,
    form.data.vehicle?.purchaseState,
    form.data.vehicle?.purchasePrice,
    form.data.dealerId,
  ]);
  //#endregion

  //#region Vehicle
  const [searchedVin, setSearchedVin] = useState(form.data?.vehicle?.vin || '');

  const vehicleDetails = useApi({ action: Vehicle.details, wait: true });

  function fetchVehicles() {
    if (searchedVin) vehicleDetails.execute({ vin: searchedVin });
    else vehicleDetails.reset();
  }
  useEffect(() => {
    if (vehicleDetails?.payload && !vehicleDetails?.isExecuting) {
      form.update({
        vehicle: vehicleDetails?.payload?.toJSON() || null,
      });
    }
  }, [vehicleDetails.payload]);
  //#endregion

  const isVehicleAgeValid = useMemo(
    () =>
      form.data.products?.some((contractProduct) => {
        if (!contractProduct?.maxVehicleAgeInYears) return true;
        const vehicleAgeInYears = dayjs().year() - form.data.vehicle?.year;
        if (!contractProduct.termInMonths) return true;
        const termInYears = contractProduct.termInMonths / 12;

        return vehicleAgeInYears + termInYears <= contractProduct?.maxVehicleAgeInYears;
      }),
    [form.data.products, form.data.vehicle?.year]
  );
  //#region Tabs
  const tabs = [
    {
      id: SalesNewContractAction.VEHICLE,
      hasErrors: !!form.errors.vehicle,
      title: !isVehicleInServiceDateValid(form.data?.vehicle) && lang.vehicleInServiceDateValidationMsg,
      label: lang.vehicleInfo,
    },
    {
      id: SalesNewContractAction.PRODUCTS,
      hasErrors: !!form.errors.dealer || !!form.errors.products?._objectError,
      label: lang.products,
    },
    {
      id: SalesNewContractAction.CONTRACT,
      hasErrors:
        !!form.errors.effectiveDate ||
        !!form.errors.expiryDate ||
        !!form.errors.priceCAD ||
        !!form.errors.salePriceCAD ||
        !isVehicleAgeValid,
      title: !isVehicleAgeValid && lang.vehicleAgeIsNotValid,
      label: lang.contract,
    },
    {
      id: SalesNewContractAction.CLIENT,
      hasErrors: !!form.errors.client,
      label: lang.client,
    },
    {
      id: SalesNewContractAction.REVIEW,
      hasErrors: !!form.errors.products?.documentsFromTemplates,
      label: lang.review,
    },
    {
      id: SalesNewContractAction.FINISH,
      hasErrors: !!form.hasErrors,
      label: lang.finish,
    },
  ];
  const activeTab = tabs.find((it) => it.id === pageRouter.params.action) ?? first(tabs);
  const nextTab = useMemo(() => tabs[tabs.findIndex((it) => it.id === activeTab.id) + 1] || last(tabs), [activeTab.id]);
  const prevTab = useMemo(
    () => tabs[tabs.findIndex((it) => it.id === activeTab.id) - 1] || first(tabs),
    [activeTab.id]
  );

  const scrollToStart = () =>
    scrollToElement({
      querySelector: `.${PageStructure.pageContent}`,
    });

  function scrollToTop() {
    const container = document.getElementById('new-contract-page');
    if (container) {
      container.scrollTop = 0;
    }
  }

  function next() {
    scrollToStart();
    if (activeTab.id === nextTab.id) addLog({ error: lang.invalidForm });
    if (nextTab.id === SalesNewContractAction.REVIEW) {
      if (form.data.vehicle.purchaseState === PurchaseState.USED) {
        const vehicle = form.data.vehicle;
        vehicle.inServiceDate = null;
        form.merge({ vehicle });
      }
    }
    if (nextTab.id === SalesNewContractAction.FINISH) {
      form.submit();
      if (!form.hasErrors) {
        pageRouter.updateParams({ action: nextTab.id, id: id?.toString() });
      }
    } else {
      pageRouter.updateParams({ action: nextTab.id, id: id?.toString() });
    }

    scrollToTop();
  }
  function prev() {
    scrollToStart();
    if (activeTab.id === nextTab.id) addLog({ error: lang.invalidForm });
    else pageRouter.updateParams({ action: prevTab.id, id: id?.toString() });

    scrollToTop();
  }
  function openTab(tab: SalesNewContractAction) {
    scrollToStart();
    if (id) {
      pageRouter.updateParams({ action: tab, id: id?.toString() });
    }
  }
  function reset() {
    setContractStatuses([]);
    setSearchedVin('');
    vehicleDetails.reset(true);
    form.reload();
    pageRouter.updateParams({ action: SalesNewContractAction.VEHICLE, id: id?.toString() });
  }
  async function saveDraft() {
    const draftForm = form.data;

    for (const draftProduct of draftForm.products ?? []) {
      for (const doc of draftProduct.documentsFromTemplates?.filter((it) => it.formFile) ?? []) {
        if (doc.formFile) {
          const mediaFileSaveResponse = await mediaFileSave({ formFile: doc.formFile });
          doc.mediaFileId = mediaFileSaveResponse.payload ?? undefined;
          doc.formFile = null;
        }
      }
    }

    const response = await draftSaveApi({
      id: pageRouter.query.draftId,
      createdById: userId,
      entityName: DRAFT_TYPE,
      data: {
        ...draftForm,
        dateUpdated: date().tz('UTC').format(defaultServerDateFormat),
      },
    });
    draftApi.execute((body) => body);
    form.update(draftForm);
    pageRouter.updateQuery({ ...pageRouter.query, draftId: response.payload });
  }
  async function deleteDraft() {
    if (!pageRouter.query.draftId) return;
    const response = await draftDeleteApi(pageRouter.query.draftId);
    if (response) {
      draftApi.execute((body) => body);
      pageRouter.updateQuery({});
      form.reload();
    }
  }
  //#endregion

  //#region DISTRIBUTOR
  useEffect(() => {
    (async () => {
      if (form.data.dealer?.representativeId) {
        const distributor = (await distributorFindApi({ id: form.data.dealer?.representativeId }))?.payload;

        form.update({ distributor });
      } else {
        form.update({ distributor: null });
      }
    })();
  }, [form.data.dealer?.representativeId]);
  //#endregion

  //#region PRODUCT
  function updateProduct(product: ContractProduct) {
    form.update({
      products: form.data.products?.map((p) => {
        if (p.id === product.id) {
          const updatedProduct = { ...p, ...product };
          updatedProduct.expiryDate =
            updatedProduct.termInMonths && updatedProduct.effectiveDate
              ? dayjs(updatedProduct.effectiveDate, defaultServerDateTimeFormat)
                  .add(updatedProduct.termInMonths, 'months')
                  .format(defaultServerDateTimeFormat)
              : null;
          if (product.pricePointId !== p.pricePointId) {
            updatedProduct.enhancementsPriceCAD = 0;
            updatedProduct.contractEnhancements = [];
          }
          return updatedProduct;
        } else return p;
      }),
    });
  }

  const [productWithPrices, setProductWithPrices] = useState<Array<ProductPricingInfo>>([]);
  const [productWithPricesLoading, setProductWithPricesLoading] = useState(false);

  function getAvailablePrices() {
    if (form.data.dealer && form.data.distributor && form.data.vehicle) {
      (async function () {
        if (form.data.dealer && form.data.distributor && form.data.vehicle) {
          setProductWithPricesLoading(true);
          const result = await calculatePricesForContract({
            dealer: form.data.dealer as Model.IDealer,
            distributor: form.data.distributor,
            vehicle: form.data.vehicle as Model.IVehicle,
          });
          unstable_batchedUpdates(() => {
            setProductWithPrices(result);
            setProductWithPricesLoading(false);
          });
        }
      })();
    }
  }

  useEffect(() => {
    updateProduct({ id: form.data.product?.id, taxExempt: form.data.client?.taxExempt });
  }, [form.data.client?.taxExempt]);
  //#endregion

  //#region LENDER
  useEffect(() => {
    (async () => {
      if (form.data.vehicle?.lenderId) {
        const lender = (await Lender.find({ id: form.data.vehicle?.lenderId }))?.payload;
        form.update({ vehicle: { ...form.data?.vehicle, lender } });
      }
    })();
  }, [form.data.vehicle?.lenderId]);
  // #endregion

  //#region WARRANTY DATE CHECK
  function isWarrantyCovered(termInMonths: string, inServiceDate: string) {
    const currentDate = new Date();
    const inServiceFromDate = new Date(inServiceDate);

    const expirationDate = new Date(inServiceFromDate);
    expirationDate.setMonth(expirationDate.getMonth() + parseInt(termInMonths));
    expirationDate.setDate(expirationDate.getDate() + 1);

    return currentDate < expirationDate;
  }

  function isVehicleNew(
    inServiceDate: string,
    posOdometerReading: number,
    mfgWholeVehicleWarrantyKm: string,
    mfgWholeVehicleWarrantyMonths: string
  ) {
    return (
      !!inServiceDate &&
      inServiceDate !== '' &&
      parseInt(mfgWholeVehicleWarrantyKm) > posOdometerReading &&
      isWarrantyCovered(mfgWholeVehicleWarrantyMonths, inServiceDate)
    );
  }

  function getMfgWholeVehicleWarranty(vehicle: Utils.FormData<Model.IVehicle>) {
    return {
      km:
        vehicle?.mfgWholeVehicleWarrantyKm ??
        vehicle?.vehicleModel?.mfgWholeVehicleWarrantyKm ??
        vehicle?.vehicleModel?.vehicleMake?.mfgWholeVehicleWarrantyKm ??
        undefined,
      months:
        vehicle?.mfgWholeVehicleWarrantyMonths ??
        vehicle?.vehicleModel?.mfgWholeVehicleWarrantyMonths ??
        vehicle?.vehicleModel?.vehicleMake?.mfgWholeVehicleWarrantyMonths ??
        undefined,
    };
  }

  function getMfgPowertrainWarranty(vehicle: Utils.FormData<Model.IVehicle>) {
    return {
      km:
        vehicle?.mfgPowertrainWarrantyKm ??
        vehicle?.vehicleModel?.mfgPowertrainWarrantyKm ??
        vehicle?.vehicleModel?.vehicleMake?.mfgPowertrainWarrantyKm ??
        undefined,
      months:
        vehicle?.mfgPowertrainWarrantyMonths ??
        vehicle?.vehicleModel?.mfgPowertrainWarrantyMonths ??
        vehicle?.vehicleModel?.vehicleMake?.mfgPowertrainWarrantyMonths ??
        undefined,
    };
  }
  //#endregion

  const isSavingDone = contractStatuses?.every(
    ({ status }) =>
      status === ContractSaveStatus.done ||
      status === ContractSaveStatus.failed ||
      status === ContractSaveStatus.consentSent ||
      status === ContractSaveStatus.consentSentFailed
  );

  async function sendContractConsent({
    contract,
    consentRequestType,
    contractIndex,
  }: {
    contract: SaveableContractData;
    consentRequestType: Model.Enum.ContractConsentRequestType;
    contractIndex: number;
  }) {
    setContractStatuses((old) => {
      old[contractIndex].contract = contract;
      old[contractIndex].status = ContractSaveStatus.sendingConsent;
      old[contractIndex].areWeSendingConsent = true;
      return [...old];
    });
    const res = await contractSendConsentApi({ contractId: contract.id, consentRequestType });
    if (res.status === HttpStatus.ok) {
      setContractStatuses((old) => {
        old[contractIndex].contract = contract;
        old[contractIndex].status = ContractSaveStatus.consentSent;
        old[contractIndex].isConsentSent = true;
        old[contractIndex].areWeSendingConsent = false;
        return [...old];
      });
      addLog({
        success:
          consentRequestType === ContractConsentRequestType.EMAIL
            ? lang.emailSuccessfullySent
            : lang.textSuccessfullySent,
      });
    } else {
      setContractStatuses((old) => {
        old[contractIndex].contract = contract;
        old[contractIndex].status = ContractSaveStatus.consentSentFailed;
        old[contractIndex].isConsentSent = false;
        old[contractIndex].areWeSendingConsent = false;
        return [...old];
      });
      addLog({ error: lang.saveError });
    }
  }

  function calculateDealerPrices(product: ContractProduct, provinceCode: string) {
    const productTax = product?.productTaxes?.find((p) => p.provinceCode === provinceCode);

    const dealerCost =
      product.originalPriceCAD +
      product.discountPriceCAD +
      product.distributorPriceCAD +
      product.surchargesPriceCAD +
      product.enhancementsPriceCAD;
    const dealerProfit = product.salePriceCAD - dealerCost;

    const dealerCostTaxAmt = dealerCost * (productTax?.dealerCostGstHst + productTax?.dealerCostProvincialSalesTax);
    const dealerProfitTaxAmt =
      dealerProfit > 0
        ? dealerProfit * (productTax?.dealerProfitGstHst + productTax?.dealerProfitProvincialSalesTax)
        : 0;

    return {
      dealerCost,
      dealerCostTaxAmt,
      dealerProfit,
      dealerProfitTaxAmt,
    };
  }

  return {
    draftApi,
    contractStatuses,
    pageRouter,
    permissions,
    productWithPrices,
    productWithPricesLoading,
    form,
    isAllowed: permissions.CREATE_CONTRACT || permissions.EDIT_CONTRACT,
    activeTab,
    nextTab,
    prevTab,
    saveDraft,
    deleteDraft,
    next,
    openTab,
    reset,
    prev,
    updateProduct,
    searchedVin,
    setSearchedVin,
    fetchVehicles,
    vehicleDetails,
    tabs,
    isSavingDone,
    isWarrantyCovered,
    isVehicleNew,
    getMfgWholeVehicleWarranty,
    getMfgPowertrainWarranty,
    isLoading: contractFindApi.isExecuting || vehicleDetails.isExecuting || draftApi.isExecuting,
    sendContractConsent,
    getAvailablePrices,
    enhancementDocs,
    calculateDealerPrices,
    isVehicleAgeValid,
    isEffectiveDateRestricted,
  };
}

export function useSalesNewContractProvider() {
  return useDataProvider<ReturnType<typeof useSalesNewContract>>();
}
